import _ from 'lodash';
import Swal from 'sweetalert2';
import * as Sentry from '@sentry/react';
import {
  V3_ADD_TO_CART,
  V3_DECLINE_INTERSTITIAL_PRODUCT,
  V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED,
} from './v3Order';

import cartUtils from '../../utils/cart';
import { productPosesCount } from '../../components/v3/products/imageRecords';
import {
  personalizationSetToSameValueSelector,
  selectIsUnconfiguredSlotBundle,
  isChildlessSlotBundle,
} from '../../selectors';
import {
  isShippingRestrictedForParent,
  availableIncentiveProducts,
} from '../../components/v3/helpers/internationalRestriction';
import { buildShootLoadParamsForOrder } from '../../components/v3/dashboard/galleryOrderContext';

const childRequiresShipping = (child) => child?.requires_shipping === true;

const selectedProductsFromProductConfig = (productConfig) =>
  Object.values(productConfig || {}).flatMap((config) => {
    const selected = [];
    if (config?.product) selected.push(config.product);
    if (Array.isArray(config?.products)) selected.push(...config.products);
    return selected;
  });

// The first pose slot the product exposes that has no selection, or null when
// every slot is filled.
//
// Checked against the product's own slot count (one per image_nodes entry), not
// against the shape of the array it was handed. An earlier version only looked
// for a gap BEFORE the last real selection, on the theory that a trailing empty
// slot was legitimate — that stopped being true once every slot became required,
// and it let three malformed shapes through for a two-slot product:
//
//   [pose, null]     -> configureCartItem throws at poses.map((p) => p.id)
//   [pose, <hole>]   -> throws at the selected_crop lookup instead (map skips
//                       holes; find does not)
//   [pose]           -> no throw at all: silently submits ONE pose id for a
//                       two-slot product
//
// Three exemptions, all deliberate:
//   * prepay never offers pose selection, so an empty array is correct there;
//   * bundle flows (BYO/YPX, multi-config) pass no `poses` at all — their
//     children's poses reach the cart through productConfig — so there is
//     nothing here to validate, and the parent's image_nodes do not describe its
//     children's slots;
//   * Geskus autoselect (a student key resolving through Datum#student_shoot_key)
//     has no gallery and no pose grid — the photo is assigned server-side and
//     the cart renders it from studentPreviewImageUrl, so `poses: []` is the
//     correct shape rather than a truncated one. Without this the gate fix alone
//     does not help: the order is refused one layer down instead.
const firstUnconfiguredPoseSlot = ({
  poses,
  product,
  isPrepay,
  posesAutoAssigned,
}) => {
  if (isPrepay || posesAutoAssigned || poses === undefined || poses === null) {
    return null;
  }

  // Array.from so holes read as undefined rather than being skipped.
  const slots = Array.from(poses);
  const expectedSlots = productPosesCount(product);

  for (let index = 0; index < expectedSlots; index++) {
    if (!slots[index]) return index;
  }

  return null;
};

const blockInternationalShippingChild = ({
  productData,
  productConfig,
  blockInternationalShipping,
}) => {
  if (!blockInternationalShipping) return false;

  const children = Array.isArray(productData?.child_products)
    ? productData.child_products
    : [];
  if (children.length === 0) return false;

  const isParent =
    productData?.product_type === 'parent_product' || productData?.is_ypx;

  if (productData?.is_multi_config === true) {
    return children.some(childRequiresShipping);
  }

  const selectedProducts = selectedProductsFromProductConfig(productConfig);
  if (isParent && selectedProducts.length > 0) {
    return selectedProducts.some(childRequiresShipping);
  }

  if (isParent) {
    return children.every(childRequiresShipping);
  }

  return children.some(childRequiresShipping);
};

export const addToCart =
  ({
    studentId,
    fullproductData,
    backgroundIds,
    backgroundSelections,
    poseIds,
    poses,
    isInterstitialProduct = false,
    quantity = 1,
    personalizationValue = null,
    redirectSource = null,
    requiredFields,
    legacyRequiredFields,
    productOptionKey,
    productOptions,
    selectedCropOptions = [],
    childCropSelections = [],
  }) =>
  async (dispatch, getState) => {
    const {
      galleryId,
      incentiveProducts,
      isPrepay,
      isPostShootOrder,
      optionalIncentivesEnabled,
      personalizationProduct,
      postpayRequiresSameValuePersonalization,
      prepayRequiresSameValuePersonalization,
      shoot,
    } = getState().shoot;

    // Ensure isPostEvent and gallery_id are set correctly for ClassAct and other gallery orders
    const additionalData = {
      ...getState().additionalData,
      isPostEvent:
        isPostShootOrder || getState().additionalData?.isPostEvent || false,
      gallery_id: galleryId || getState().additionalData?.gallery_id || null,
    };

    // selectInitialStepsComplete is what gates Add to Cart; this is the backstop
    // for a half-configured product slipping past it. Build nothing:
    // configureCartItem would either throw on the empty slot or quietly submit
    // fewer pose ids than the product has slots.
    const unconfiguredSlot = firstUnconfiguredPoseSlot({
      poses,
      product: fullproductData,
      isPrepay,
      // Same rule as the gate's posesAutoAssigned (selectors/productConfig.ts).
      posesAutoAssigned: Boolean(
        additionalData.isGeskus &&
          !additionalData.picsGalleryId &&
          additionalData.studentPreviewImageUrl,
      ),
    });
    if (unconfiguredSlot !== null) {
      Sentry.captureMessage(
        `Add to cart blocked: product ${fullproductData?.id} ` +
          `("${fullproductData?.name}") has no pose selected for slot ` +
          `${unconfiguredSlot + 1} of ${productPosesCount(fullproductData)}`,
      );
      // Deliberately NOT the gate's own wording ("Please finish configuring
      // your product", in AddToCartButton): naming the slot tells the customer
      // what to do and lets QA tell the two failure modes apart.
      return Swal.fire(
        `Please choose a photo for item ${
          unconfiguredSlot + 1
        } before adding this to your cart.`,
      );
    }

    const { orders, productConfig, optionalIncentiveToConfigure } =
      getState().v3Order;

    // A you-pick / build-your-own package with no items chosen came in through
    // the regular product config and would reach the lab empty (ENG-1683).
    // Checkout refuses it too; refusing here keeps it out of the cart, and the
    // Sentry message says some route still leads a package there.
    if (selectIsUnconfiguredSlotBundle(getState(), fullproductData)) {
      // A package the price list left with no child products cannot be chosen
      // from at all, so "choose the items" is advice the customer cannot take
      // and re-adding it fails the same way. Name it as unavailable instead,
      // and tell Sentry which of the two it is so the price list gets fixed.
      if (isChildlessSlotBundle(fullproductData)) {
        Sentry.captureMessage(
          `Add to cart blocked: package ${fullproductData.id} ` +
            `("${fullproductData.name}") has no child products in the price list`,
        );
        return Swal.fire(
          'Product not purchasable',
          'This package is not available to order right now. Please choose a different product, or contact us if you need help.',
          'warning',
        );
      }
      Sentry.captureMessage(
        `Add to cart blocked: package ${fullproductData.id} ` +
          `("${fullproductData.name}") has no items chosen`,
      );
      return Swal.fire(
        'Please choose the items for this package before adding it to your cart.',
      );
    }
    const { selectedStudent } = getState().additionalData;
    const restricted = isShippingRestrictedForParent(shoot, getState().parent);
    const availableIncentives = availableIncentiveProducts(
      incentiveProducts,
      restricted,
    );
    const incentiveProductIds = availableIncentives.map(
      (product) => product.id,
    );

    const orderKey = `${shoot?.event_id || shoot?.id}-${selectedStudent?.id}`;
    const currentOrder = orders[orderKey];
    const cartItems = currentOrder?.cartItems || [];

    const cartItemsIncludeRetouching =
      cartUtils.cartItemsIncludeRetouching(cartItems);
    const cartItemsContainRetouchingProduct =
      cartUtils.cartItemsContainRetouchingProduct(cartItems);

    const formattedRequiredFields = [];
    let requiredProductFields = fullproductData.required_product_fields;
    const childRequiredFields = (fullproductData.child_products || [])
      .map((child) => child.required_product_fields)
      .flat()
      .filter((el) => el !== null);

    requiredProductFields = (requiredProductFields || [])
      .concat(childRequiredFields)
      .filter((el) => el !== undefined && el !== null);

    const legacyRequiredProductFields =
      fullproductData.legacy_required_product_fields;

    if (Array.isArray(requiredProductFields)) {
      requiredProductFields.forEach((field) => {
        const productField = {
          id: field.id,
          key: field.key,
          name: field.name,
          label: field.label,
          value: requiredFields[field.key],
        };
        formattedRequiredFields.push(productField);
      });
    }

    const formattedLegacyRequiredFields = (
      legacyRequiredProductFields || []
    ).reduce((acc, field) => {
      acc[field.name] = legacyRequiredFields[field.name];
      return acc;
    }, {});

    // When adding the retouching product, ensure it's not in the cart first
    if (fullproductData.is_retouching) {
      if (cartItemsIncludeRetouching || cartItemsContainRetouchingProduct) {
        const message = cartItemsIncludeRetouching
          ? 'One of the items in the cart already includes retouching :)'
          : 'You already have retouching added to your order :)';

        return Swal.fire({
          title: 'Retouching already added',
          text: message,
          icon: 'info',
          // showCancelButton: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'OK',
        });
      }
    }

    // TODO: don't allow users to add personalization more than once
    // TODO: don't allow users to add retouching to an empty cart
    // TODO: don't allow users to add personalization to an empty cart

    // TODO: describe what this does
    const personalizationSetToSameValue = personalizationSetToSameValueSelector(
      getState(),
    );

    // TODO: extract this to a utility function (same as above)
    // Specify the keys we want to store in the cart
    const productData = _.pick(fullproductData, [
      'additional_data',
      'background_options_count',
      'contents',
      'id',
      'image_thumbnail',
      'image_nodes',
      'incentive_applies_to',
      'incentive_threshold',
      'incentive_applies_to',
      'includes_retouching',
      'is_byo',
      'is_retouching',
      'is_personalization',
      'is_ypx',
      'is_multi_config',
      'lock_group',
      'lock_type',
      'personalization_status',
      'poses_count',
      'price',
      'product_type',
      'name',
      'nth_free_amount_allowed',
      'nth_free_product_id',
      'nth_free_sku',
      'nth_free_threshold',
      'nth_free_value',
      'is_pre_cart_offer',
      'requires_shipping',
      'sku',
      'child_products',
      'required_product_fields',
      'product_options',
      'product_option_sets',
    ]);

    const isPreCartOfferProduct = productData.is_pre_cart_offer;

    let personalizationValueForCart = null;

    // First look through productConfig, which is what is submitted for YPX and BYO products
    // This case handles orders where the personalization value is set to the same value for all products
    // TODO: handle cases when YPX/BYO products can have different personalization values
    if (
      personalizationSetToSameValue &&
      productConfig &&
      productConfig.length > 0
    ) {
      let personalizationFromProductConfig = null;

      productConfig.forEach((config) => {
        if (config.personalization) {
          personalizationFromProductConfig = config.personalization;
        }
      });

      if (
        personalizationFromProductConfig !== null &&
        personalizationFromProductConfig !== ''
      ) {
        personalizationValueForCart = personalizationFromProductConfig;
      }
    }

    // Look at personalizationValue, which is set on the submitted item
    if (
      personalizationValue &&
      personalizationValue !== '' &&
      personalizationValue !== null
    ) {
      personalizationValueForCart = personalizationValue;
    }

    // We need to use the event_id if it exists, otherwise use the shoot id
    // DP2 shoot ids are different from the shoot (event) id
    const eventId = shoot?.event_id || shoot?.id;

    const cartObject = cartUtils.configureCartItem({
      backgroundIds,
      backgroundSelections,
      incentiveProductIds,
      productConfig,
      productData,
      poses,
      poseIds,
      personalizationValueForCart,
      personalizationSetToSameValue,
      quantity,
      requiredFields: formattedRequiredFields,
      legacyRequiredFields: formattedLegacyRequiredFields,
      productOptionKey,
      productOptions,
      eventId,
      selectedCropOptions,
      childCropSelections,
      blockInternationalShipping: shoot?.block_international_shipping === true,
      blockInternationalShippingChild: blockInternationalShippingChild({
        productData,
        productConfig,
        blockInternationalShipping:
          shoot?.block_international_shipping === true,
      }),
    });

    // TODO: revisit this logic -- it's a bit confusing to
    // call 'decline' when the product is being added to the cart...
    // we should probably create an action to support this case,
    // something like V3_MARK_INTERSTITIAL_PRODUCT_AS_PROMPTED, etc.
    if (isInterstitialProduct) {
      dispatch({
        type: V3_DECLINE_INTERSTITIAL_PRODUCT,
        payload: { orderKey, productId: cartObject.productId },
      });
    }

    // A marker only names a re-add while the order it names is STILL in the
    // cart and still belongs to the shoot this add is being made from. The
    // marker is redux-persisted and cleared only by the configurator, so a
    // parent who left the configurator by an in-app link can arrive here —
    // days later, from another student's gallery — with a marker for that
    // earlier order; honouring it would file the bonus, configured from THIS
    // gallery's images, in the other order (ENG-2043 review F5). Both re-add
    // tiers that set a marker guarantee the active shoot is the marked order's
    // shoot by the time the add happens (tier 3 loads it first), so this
    // rejects only markers that have gone stale.
    const markerOrder = optionalIncentiveToConfigure?.orderKey
      ? orders[optionalIncentiveToConfigure.orderKey]
      : null;
    const markerNamesLiveOrder = Boolean(
      markerOrder && markerOrder.event?.id === eventId,
    );

    // The pending cart re-add this add completes, if any: the marker names the
    // order it belongs to, and the product matches by id (or, for a
    // cart-level benefit, by sku — see optionalIncentiveMarkerMatchesProduct).
    const optionalIncentiveReAdd =
      markerNamesLiveOrder &&
      cartUtils.optionalIncentiveMarkerMatchesProduct(
        optionalIncentiveToConfigure,
        {
          productId: cartObject.productId,
          sku: cartObject.sku ?? null,
          incentiveAppliesTo: cartObject.incentiveAppliesTo,
        },
      )
        ? {
            orderKey: optionalIncentiveToConfigure.orderKey,
            productId: cartObject.productId,
            sku: cartObject.sku ?? null,
          }
        : null;

    // A cart-level bonus belongs to the order whose price list AWARDED it, not
    // to whichever gallery happens to be open (ENG-2043 R3, review F3). The
    // picker offers cart-level rows from a union across every price list in
    // the cart, so the row on screen may be another studio's — free shipping
    // is a row on 500+ lists across eight companies. Filing it in the active
    // order left a foreign-company row in that order, which the submit gate
    // refused with no client-side way out. Order-level bonuses come from the
    // order being configured, and a row nobody owns keeps today's behaviour.
    const cartLevelIncentiveOwner =
      !optionalIncentiveReAdd &&
      cartObject.isIncentive === true &&
      cartObject.incentiveAppliesTo === 'cart_level'
        ? cartUtils.ownerOrderForCartIncentive(orders, {
            id: productData.id,
            // Not on the cart pick list above, and the price-list branch of
            // the owner lookup needs it.
            company_pricing_model_id:
              fullproductData?.company_pricing_model_id ?? null,
          })
        : null;

    const ownerOrderKey =
      cartLevelIncentiveOwner && cartLevelIncentiveOwner.orderKey !== orderKey
        ? cartLevelIncentiveOwner.orderKey
        : null;

    // Which order this add is filed under, when it is not the active one.
    // `handleAddToCart` honours `payload.orderKey`.
    let orderRouting = {};
    if (optionalIncentiveReAdd) {
      orderRouting = {
        orderKey: optionalIncentiveReAdd.orderKey,
        optionalIncentiveReAdd,
      };
    } else if (ownerOrderKey) {
      orderRouting = { orderKey: ownerOrderKey };
    }

    const ownerOrder = ownerOrderKey ? cartLevelIncentiveOwner.order : null;

    // The item carries the event of the order it lands in: the server resolves
    // shoot restrictions from `cart_item['eventId']`.
    const cartObjectForOrder = ownerOrder
      ? { ...cartObject, eventId: ownerOrder.event?.id ?? cartObject.eventId }
      : cartObject;

    // `handleAddToCart` stamps the payload's context onto the order it files
    // the item in — `additionalData` outright, the incentive stashes, the
    // opt-in flag and the shoot load params through `??`/`||`. Every one of
    // those describes the ACTIVE shoot, so an add routed to another studio's
    // order has to hand that order its own values back; otherwise filing a
    // free-shipping row would re-stamp a mandatory studio's order as opt-in
    // and replace its catalog (ENG-2043 R1/R2 — an order answers for itself).
    const ownerOrderContext = ownerOrder
      ? {
          additionalData: ownerOrder.additionalData ?? additionalData,
          isPrepay: ownerOrder.isPrepay === true,
          cartLevelIncentiveProducts: ownerOrder.incentiveProducts ?? [],
          orderLevelIncentiveProducts:
            ownerOrder.orderLevelIncentiveProducts ?? [],
          optionalIncentivesEnabled:
            ownerOrder.optionalIncentivesEnabled === true,
          shootLoadParams: ownerOrder.shootLoadParams ?? null,
        }
      : {};

    let payload = {
      additionalData,
      cartObject: cartObjectForOrder,
      isPrepay,
      personalizationSetToSameValue,
      selectedStudent,
      shoot,
      redirectSource,
      requiredFields: formattedRequiredFields,
      // Stashed on the order so cart-level incentives from this shoot's
      // price list stay offerable after another shoot's catalog overwrites
      // state.shoot.incentiveProducts (ENG-2965). Each entry carries its
      // shoot's restriction flag so unionCartLevelIncentiveProducts can
      // re-apply the international rule to stashed entries later.
      cartLevelIncentiveProducts: incentiveProducts
        .filter((product) => product.incentive_applies_to === 'cart_level')
        .map((product) => ({
          ...product,
          block_international_shipping:
            shoot?.block_international_shipping === true,
        })),
      // This shoot's ORDER-level incentive rows, stashed separately from the
      // cart-level stash above so the ENG-2965 union keeps its meaning
      // (ENG-2043 R2). Without them an order from a non-active shoot has no
      // catalog of its own and its declined bonuses cannot be listed.
      orderLevelIncentiveProducts: incentiveProducts
        .filter((product) => product.incentive_applies_to !== 'cart_level')
        .map((product) => ({
          ...product,
          block_international_shipping:
            shoot?.block_international_shipping === true,
        })),
      // Whether the company that owns THIS order allows bonuses to be
      // declined / removed. Read per order from now on: the active shoot's
      // flag belongs to the last-opened gallery, not to this order (R1).
      optionalIncentivesEnabled: optionalIncentivesEnabled === true,
      // Enough context to reload this order's shoot from the cart later
      // (re-add tiers); written once, on the order's first add.
      shootLoadParams: buildShootLoadParamsForOrder({
        shoot,
        additionalData,
        selectedStudent,
      }),
      // Completing a re-add started from the cart's Available Bonuses list,
      // or filing a cart-level bonus under its owner order. The re-add flag is
      // carried on the ACTION rather than left for readers to infer from
      // `optionalIncentiveToConfigure`, because the reducer clears that
      // marker as part of this very dispatch and postConfigRoutingMiddleware
      // reads state only afterwards (ENG-2043 R7). `orderKey` pins the add to
      // the order that earned the bonus, so a re-add for another student of
      // the same shoot cannot spawn a phantom order.
      ...orderRouting,
      ...ownerOrderContext,
    };

    const cartObjectChildPersonalizationValues = (cartObject.children || [])
      .map((child) => child.personalizationValue)
      .filter((value) => value !== undefined && value !== null && value !== '');

    // If the cart object or its child products are personalized, send the personalization product (if it exists)
    if (
      personalizationProduct &&
      (cartObject.personalizationValue ||
        cartObjectChildPersonalizationValues.length > 0)
    ) {
      // The enhancement is added BECAUSE a personalization value was entered:
      // on the product itself, or -- for a YPX/BYO bundle -- on one of its
      // configured children, whose values live in productConfig rather than on
      // the parent. The enhancement is `personalization_required` in the price
      // list, so it has to carry that value: an empty one is refused at
      // pay-and-submit by OrderSubmit::CheckForMissingPersonalization, with the
      // customer told to "enter a personalization value for all required
      // products" for a value they already entered (ENG-2936).
      //
      // The middle term is inert today: cartObject.personalizationValue is
      // either personalizationValueForCart or the falsy result of the inverted
      // `find((p) => !p)` in configureCartItem. It becomes the correct source
      // for a bundle once ENG-2938 fixes that predicate -- it is kept, not
      // dead weight to delete.
      const personalizationValueForEnhancement =
        personalizationValueForCart ||
        cartObject.personalizationValue ||
        cartObjectChildPersonalizationValues[0];

      // TODO: extract this to a utility function (same as above)
      const personalizationProductData = _.pick(personalizationProduct, [
        'additional_data',
        'background_options_count',
        'contents',
        'id',
        'image_thumbnail',
        'image_nodes',
        'incentive_applies_to',
        'incentive_threshold',
        'includes_retouching',
        'is_byo',
        'is_retouching',
        'is_personalization',
        'is_ypx',
        'is_multi_config',
        'lock_group',
        'lock_type',
        'personalization_status',
        'poses_count',
        'price',
        'product_type',
        'name',
        'nth_free_amount_allowed',
        'nth_free_product_id',
        'nth_free_sku',
        'nth_free_threshold',
        'nth_free_value',
        'requires_shipping',
        'sku',
        'child_products',
        'required_product_fields',
        'product_options',
      ]);

      const configuredPersonalizationProduct = cartUtils.configureCartItem({
        backgroundIds: [],
        backgroundSelections: [],
        incentiveProductIds,
        productConfig,
        productData: personalizationProductData,
        poses: [],
        poseIds: [],
        personalizationValueForCart: personalizationValueForEnhancement,
        personalizationSetToSameValue,
        quantity,
        requiredFields: formattedRequiredFields,
        legacyRequiredFields: formattedLegacyRequiredFields,
        productOptionKey: null, // TODO: confirm null works here
        productOptions: {},
        eventId,
        blockInternationalShipping:
          shoot?.block_international_shipping === true,
        blockInternationalShippingChild: blockInternationalShippingChild({
          productData: personalizationProductData,
          productConfig,
          blockInternationalShipping:
            shoot?.block_international_shipping === true,
        }),
      });

      payload = {
        ...payload,
        personalizationProduct: configuredPersonalizationProduct,
      };
    }

    if (isPreCartOfferProduct) {
      dispatch({
        type: V3_MARK_PRE_CART_OFFER_PRODUCT_AS_PROMPTED,
        payload: { orderKey, productId: cartObject.productId },
      });
    }

    dispatch({
      type: V3_ADD_TO_CART,
      payload,
    });
  };
